/**
 * Visual feedback system for KeyboardHero
 * Handles background flashes and radial waves
 * 
 * 🚀 PERFORMANCE OPTIMIZED: Replaced React createRoot() per-keystroke with pure CSS animations
 */

export interface VisualFeedbackConfig {
  enableBackgroundFlash: boolean;
  enableRadialWaves: boolean;
  flashDuration: number;
  waveDuration: number;
  useMotionAnimation: boolean;
}

class VisualFeedbackManager {
  private config: VisualFeedbackConfig;
  private flashElement: HTMLElement | null = null;
  private isRedPersistent: boolean = false;
  private activeTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor(config: VisualFeedbackConfig) {
    this.config = config;
    this.createFlashElement();
  }

  private createFlashElement() {
    this.flashElement = document.createElement('div');
    this.flashElement.id = 'keyboard-hero-flash';
    this.flashElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.1s ease-out;
    `;
    document.body.appendChild(this.flashElement);
  }

  public triggerCorrectFeedback() {
    if (!this.config.enableBackgroundFlash) return;

    this.clearRedBackground();
    this.flashGreen();
    
    if (this.config.enableRadialWaves) {
      this.createRadialWave('correct');
    }
  }

  public triggerIncorrectFeedback() {
    if (!this.config.enableBackgroundFlash) return;

    this.flashRed();
    this.setRedPersistent();
    
    if (this.config.enableRadialWaves) {
      this.createRadialWave('incorrect');
    }
  }

  public clearRedBackground() {
    this.isRedPersistent = false;
    if (this.flashElement) {
      this.flashElement.style.background = 'transparent';
      this.flashElement.style.opacity = '0';
    }
  }

  private flashGreen() {
    if (!this.flashElement) return;

    this.flashElement.style.background = 'rgba(34, 197, 94, 0.3)';
    this.flashElement.style.opacity = '1';

    const t1 = setTimeout(() => {
      if (this.flashElement) {
        this.flashElement.style.opacity = '0';
      }
    }, this.config.flashDuration);
    this.activeTimeouts.push(t1);

    const t2 = setTimeout(() => {
      if (this.flashElement && !this.isRedPersistent) {
        this.flashElement.style.background = 'transparent';
      }
    }, this.config.flashDuration + 100);
    this.activeTimeouts.push(t2);
  }

  private flashRed() {
    if (!this.flashElement) return;

    this.flashElement.style.background = 'rgba(239, 68, 68, 0.4)';
    this.flashElement.style.opacity = '1';
  }

  private setRedPersistent() {
    this.isRedPersistent = true;
    if (this.flashElement) {
      this.flashElement.style.background = 'rgba(239, 68, 68, 0.2)';
      this.flashElement.style.opacity = '1';
    }
  }

  /**
   * 🚀 PERFORMANCE OPTIMIZATION: Pure CSS radial wave animation
   * Replaces the old approach of calling React's createRoot() + render() + unmount()
   * on EVERY keystroke, which was the #1 cause of training mode lag.
   * 
   * Now uses a simple DOM element with CSS transition for hardware-accelerated animation.
   */
  private createRadialWave(type: 'correct' | 'incorrect') {
    if (!this.config.enableRadialWaves) return;

    // Find the letter display container to position animation behind it
    const letterDisplay = document.querySelector('.letter-display-container');
    if (!letterDisplay) return;

    const letterRect = letterDisplay.getBoundingClientRect();
    const color = type === 'correct' ? '34, 197, 94' : '239, 68, 68';
    
    const ring = document.createElement('div');
    ring.style.cssText = `
      position: fixed;
      top: ${letterRect.top + letterRect.height / 2}px;
      left: ${letterRect.left + letterRect.width / 2}px;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 140;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: 3px solid rgba(${color}, 0.8);
      box-shadow: 0 0 20px rgba(${color}, 0.4), 0 0 60px rgba(${color}, 0.2);
      opacity: 1;
      will-change: width, height, opacity, border-width;
    `;

    document.body.appendChild(ring);

    // Trigger CSS transition on next frame
    requestAnimationFrame(() => {
      ring.style.transition = `all ${this.config.waveDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
      ring.style.width = '500px';
      ring.style.height = '500px';
      ring.style.marginLeft = '-250px';
      ring.style.marginTop = '-250px';
      ring.style.borderWidth = '1px';
      ring.style.opacity = '0';
    });

    // Cleanup after animation completes
    const timeoutId = setTimeout(() => {
      if (ring.parentNode) {
        ring.parentNode.removeChild(ring);
      }
    }, this.config.waveDuration + 100);
    this.activeTimeouts.push(timeoutId);
  }

  public updateConfig(newConfig: Partial<VisualFeedbackConfig>) {
    this.config = { ...this.config, ...newConfig };
  }

  public cleanup() {
    this.activeTimeouts.forEach(id => clearTimeout(id));
    this.activeTimeouts = [];

    if (this.flashElement && this.flashElement.parentNode) {
      this.flashElement.parentNode.removeChild(this.flashElement);
      this.flashElement = null;
    }
  }

  public isPersistentRed(): boolean {
    return this.isRedPersistent;
  }
}

const DEFAULT_VISUAL_CONFIG: VisualFeedbackConfig = {
  enableBackgroundFlash: true,
  enableRadialWaves: true,
  flashDuration: 150,
  waveDuration: 600,
  useMotionAnimation: true,
};

let visualManager: VisualFeedbackManager | null = null;

export const getVisualFeedbackManager = (): VisualFeedbackManager => {
  if (!visualManager) {
    visualManager = new VisualFeedbackManager(DEFAULT_VISUAL_CONFIG);
  }
  return visualManager;
};

export { VisualFeedbackManager };